import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { iconSaveGamification } from '../../assets';
import { GAMIFICATION_VALIDITY_DAYS_MAX } from '../../gamificationConfigTypes';
import { useTheme } from '../../theme';

const VALIDITY_DAYS_MAX_DIGITS = 3;

export interface GamificationEngineSettingsProps {
  pointsValidityDays: number;
  onPointsValidityDaysChange: (value: number) => void;
  onSave: () => void;
  loading?: boolean;
  saving?: boolean;
  /** Chave i18n ou null para ocultar */
  errorKey?: string | null;
}

export function GamificationEngineSettings({
  pointsValidityDays,
  onPointsValidityDaysChange,
  onSave,
  loading = false,
  saving = false,
  errorKey = null,
}: GamificationEngineSettingsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const g = theme.palette.adminGamification;

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value
      .replace(/\D/g, '')
      .slice(0, VALIDITY_DAYS_MAX_DIGITS);
    if (digits === '') {
      onPointsValidityDaysChange(0);
      return;
    }
    const n = parseInt(digits, 10);
    if (Number.isFinite(n)) {
      onPointsValidityDaysChange(Math.min(n, GAMIFICATION_VALIDITY_DAYS_MAX));
    }
  };

  const displayDays = Math.min(
    GAMIFICATION_VALIDITY_DAYS_MAX,
    Number.isFinite(pointsValidityDays) && pointsValidityDays >= 0
      ? pointsValidityDays
      : 0
  );

  return (
    <section
      style={{
        backgroundColor: g.backgroundCard,
        border: `${theme.shape.borderWidth.thin} solid ${g.cardBorder}`,
        borderRadius: g.borderRadiusCard,
        padding: g.paddingCard,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      {errorKey ? (
        <p
          role="alert"
          style={{
            color: theme.palette.error.main,
            fontSize: theme.typography.fontSize.sm,
            lineHeight: theme.typography.lineHeight.normal,
            margin: `0 0 ${g.errorAlertMarginBottom} 0`,
          }}
        >
          {t(`gamification.error.${errorKey}`)}
        </p>
      ) : null}

      <h3
        style={{
          margin: `0 0 ${theme.spacing.sm2} 0`,
          fontSize: theme.typography.fontSize.base,
          fontWeight: theme.typography.fontWeight.semibold,
          lineHeight: theme.typography.lineHeight.tight,
          color: g.cardTitleText,
        }}
      >
        {t('gamification.validity.title')}
      </h3>
      <p
        style={{
          margin: `0 0 ${theme.spacing.md} 0`,
          fontSize: theme.typography.fontSize.sm,
          lineHeight: theme.typography.lineHeight.normal,
          color: g.validityDescriptionText,
        }}
      >
        {t('gamification.validity.description')}
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: g.inputRowGap,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={VALIDITY_DAYS_MAX_DIGITS}
          disabled={loading}
          value={String(displayDays)}
          onChange={onInputChange}
          aria-label={t('gamification.validity.title')}
          style={{
            width: g.inputWidthPx,
            flexShrink: 0,
            minHeight: g.inputMinHeight,
            padding: `0 ${g.inputPaddingX}`,
            borderRadius: g.inputBorderRadius,
            border: `1px solid ${g.inputBorder}`,
            backgroundColor: g.inputBackground,
            color: g.inputText,
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.medium,
            lineHeight: theme.typography.lineHeight.normal,
            boxSizing: 'border-box',
            textAlign: 'center',
            outline: 'none',
            boxShadow: 'none',
          }}
        />
        <span
          style={{
            flexShrink: 0,
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.medium,
            lineHeight: theme.typography.lineHeight.normal,
            color: g.cardTitleText,
          }}
        >
          {t('gamification.validity.suffix')}
        </span>
      </div>
      <div
        style={{
          marginTop: g.infoBannerMarginTop,
          padding: g.infoBannerPadding,
          borderRadius: g.infoBannerBorderRadius,
          backgroundColor: g.infoBannerBg,
          border: `1px solid ${g.infoBannerBorder}`,
          fontSize: theme.typography.fontSize.sm,
          lineHeight: theme.typography.lineHeight.normal,
          color: g.infoBannerText,
          fontWeight: theme.typography.fontWeight.medium,
        }}
      >
        {t('gamification.validity.info', { days: displayDays })}
      </div>

      <footer
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: g.footerMarginTop,
        }}
      >
        <button
          type="button"
          disabled={loading || saving}
          onClick={onSave}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: g.saveButtonGap,
            minHeight: g.saveButtonMinHeight,
            padding: `${g.saveButtonPaddingY} ${g.saveButtonPaddingX}`,
            borderRadius: g.saveButtonBorderRadius,
            border: 'none',
            cursor: loading || saving ? 'not-allowed' : 'pointer',
            opacity: loading || saving ? 0.75 : 1,
            backgroundColor: g.saveButtonBackground,
            color: g.saveButtonText,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold,
            lineHeight: theme.typography.lineHeight.normal,
          }}
        >
          <img
            src={iconSaveGamification}
            alt=""
            width={g.saveIconSize}
            height={g.saveIconSize}
            style={{ objectFit: 'contain', display: 'block' }}
          />
          {saving
            ? t('gamification.actions.saving')
            : t('gamification.actions.save')}
        </button>
      </footer>
    </section>
  );
}
